import { useQuery } from '@tanstack/react-query'
import { useUser } from '~/features/auth/api/useUser'

/** `Deposit.Status.PENDING` on the server -- the rows this UI labels "Confirming". */
const CONFIRMING = 1

/** How often to re-ask while something is still confirming. */
const CONFIRMING_POLL_MS = 5000

/**
 * The deposit log, kept live only while something in it is still moving.
 *
 * This list used to be fetched once and then left alone. A row turned green solely because of the
 * `setInterval` that `useConfirmTransaction` starts in the tab that made the deposit -- so reload
 * the page, open it on another device, or come back tomorrow, and a deposit that had long since
 * landed still read "Confirming" until something else happened to invalidate the query.
 *
 * That was survivable while the row was a static badge. It is not survivable next to a progress
 * bar: an animation that keeps filling after the money already arrived is worse than no animation
 * at all, because it is actively wrong rather than merely uninformative.
 *
 * Polling is conditional on purpose. A page of settled rows is a log -- it cannot change on its
 * own, so re-asking for it is traffic that buys nothing.
 */
export const useDepositList = ({ page = 1 }) => {
    const { data: user } = useUser()
    return useQuery<DeposiListResponse>({
        queryKey: ['deposit.solana_usdc.list', page],
        queryFn: async () => {
            const params = new URLSearchParams({ page: page.toString() })
            const res = await fetch('/api/bank/deposit/solana_usdc/list?' + params, {})
            const data = await res.json()
            return data
        },
        enabled: user.is_auth,
        keepPreviousData: true,
        refetchInterval: (data) =>
            data?.data?.some((deposit) => deposit.status === CONFIRMING) ? CONFIRMING_POLL_MS : false,
    })
}
